import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import { CreateUserButton } from "@/components/create-user";
import { useAdminUsers, useResetPassword, useDeleteUser } from "@/api/hooks";
import { relativeTime } from "@/lib/format-date";
import { Copy, Check, KeyRound, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { ResetPasswordResponse } from "@/api/types";

export default function UserManagementPage() {
  const { data, isLoading, isError, error, refetch } = useAdminUsers();
  const resetPassword = useResetPassword();
  const deleteUser = useDeleteUser();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [resetResult, setResetResult] = useState<ResetPasswordResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const filtered = (data?.data ?? []).filter((u) =>
    u.username.toLowerCase().includes(search.toLowerCase())
  );

  async function handleReset(username: string) {
    const result = await resetPassword.mutateAsync(username);
    setResetResult(result);
  }

  async function handleDelete(username: string) {
    await deleteUser.mutateAsync(username);
    setPendingDelete(null);
    queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  }

  async function copyCredentials() {
    if (!resetResult) return;
    await navigator.clipboard.writeText(
      `Username: ${resetResult.username}\nPassword: ${resetResult.password}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (isLoading) return <Spinner />;
  if (isError) return <ErrorDisplay message={error.message} onRetry={() => refetch()} />;

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#1E293B]">User Management</h1>
        <div className="flex items-center gap-3">
          {data && (
            <span className="text-sm text-muted-foreground">{data.count} users</span>
          )}
          <CreateUserButton />
        </div>
      </div>

      <Input
        placeholder="Search by username..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      {filtered.length === 0 ? (
        <EmptyState message="No users found" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last Modified</TableHead>
              <TableHead>Devices</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((user) => (
              <TableRow key={user.username}>
                <TableCell className="font-mono text-xs">{user.username}</TableCell>
                <TableCell>
                  <Badge variant={user.status === "CONFIRMED" ? "default" : "secondary"}>
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {user.created_at ? relativeTime(user.created_at) : "-"}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {user.last_modified ? relativeTime(user.last_modified) : "-"}
                </TableCell>
                <TableCell>{user.device_count}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Reset password"
                      onClick={() => handleReset(user.username)}
                      disabled={resetPassword.isPending}
                    >
                      <KeyRound className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Delete user"
                      onClick={() => setPendingDelete(user.username)}
                    >
                      <Trash2 className="size-3.5 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Reset password result dialog */}
      <Dialog open={!!resetResult} onOpenChange={(open) => !open && setResetResult(null)}>
        <DialogContent onClose={() => setResetResult(null)}>
          <DialogHeader>
            <DialogTitle>Password Reset</DialogTitle>
            <DialogDescription>New credentials for {resetResult?.username}</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-muted p-3 font-mono text-sm space-y-1">
            <div>Username: <span className="font-semibold">{resetResult?.username}</span></div>
            <div>Password: <span className="font-semibold">{resetResult?.password}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={copyCredentials} size="sm">
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button onClick={() => setResetResult(null)} size="sm">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-mono font-semibold">{pendingDelete}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && handleDelete(pendingDelete)}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
